import React, { useState, useEffect } from 'react';
import {
  collection, query, where, onSnapshot,
  doc, updateDoc, setDoc, getDoc,
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useOrgId } from '../../context/OrgContext';
import { useOrgData } from '../../hooks/useOrgData';
import { logAudit } from '../../utils/auditLog';

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const CURRENT_MONTH = new Date().toISOString().substring(0, 7);

const Reconcile = () => {
  const orgId = useOrgId();
  const { accounts, loading: orgLoading } = useOrgData(orgId);
  const activeAccounts = accounts.filter(a => a.active !== false);

  const [account, setAccount] = useState('');
  const [month, setMonth] = useState(CURRENT_MONTH);
  const [statementBalance, setStatementBalance] = useState('');
  const [beginningBalance, setBeginningBalance] = useState('');
  const [transactions, setTransactions] = useState([]);
  const [cleared, setCleared] = useState({}); // txId → boolean, mirrors each transaction's own `cleared` field
  const [saving, setSaving] = useState(false);
  const [reconciliation, setReconciliation] = useState(null); // saved record
  const [outstanding, setOutstanding] = useState([]); // uncleared txns for this account sitting in a different statement month
  const [showOutstanding, setShowOutstanding] = useState(false);
  const [movingTxId, setMovingTxId] = useState(null);
  const [moveToMonth, setMoveToMonth] = useState('');

  // Set default account once loaded
  useEffect(() => {
    if (!account && activeAccounts.length > 0) setAccount(activeAccounts[0].name);
  }, [activeAccounts, account]);

  // Load saved reconciliation record for this account+month
  useEffect(() => {
    if (!account || !month) return;
    const id = `${account}__${month}`;
    getDoc(doc(db, 'orgs', orgId, 'reconciliations', id)).then((snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setReconciliation(data);
        setStatementBalance(data.statementBalance?.toString() || '');
        setBeginningBalance(data.beginningBalance?.toString() || '');
      } else {
        setReconciliation(null);
        setStatementBalance('');
        // Auto-calculate beginning balance from prior month's ending balance
        calculateBeginningBalance(account, month);
      }
    });
  }, [account, month]); // eslint-disable-line react-hooks/exhaustive-deps

  const calculateBeginningBalance = async (acct, mnth) => {
    // Look for the most recent prior reconciliation for this account
    const [year, mo] = mnth.split('-').map(Number);
    const prevMonth = mo === 1
      ? `${year - 1}-12`
      : `${year}-${String(mo - 1).padStart(2, '0')}`;
    const prevId = `${acct}__${prevMonth}`;
    const prevSnap = await getDoc(doc(db, 'orgs', orgId, 'reconciliations', prevId));
    if (prevSnap.exists()) {
      setBeginningBalance(prevSnap.data().endingBalance?.toString() || '0');
    } else {
      // Fall back to Starting Balance transactions for this account
      setBeginningBalance('0');
    }
  };

  // Load transactions for this account/month
  useEffect(() => {
    if (!account || !month) return;
    const year = Number(month.substring(0, 4));
    // Filter by account/month and sort client-side (rather than adding more
    // `where`/`orderBy` clauses) so this doesn't depend on a composite Firestore
    // index — a single equality filter is covered by Firestore's automatic indexes.
    const q = query(
      collection(db, 'orgs', orgId, 'transactions'),
      where('year', '==', year),
    );
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const filtered = all
        .filter(t => t.type !== 'Starting Balance' && t.account === account && (t.stmtMonth || t.date?.substring(0, 7)) === month)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setTransactions(filtered);
      // The transaction's own `cleared` field is the source of truth, so this
      // stays in sync with edits made from the Transactions page too.
      const clearedMap = {};
      filtered.forEach(t => { clearedMap[t.id] = !!t.cleared; });
      setCleared(clearedMap);
    }, (err) => {
      console.error('Reconcile query error:', err.code, err.message);
    });
    return unsub;
  }, [account, month]); // eslint-disable-line react-hooks/exhaustive-deps

  // Uncleared transactions for this account sitting in a *different* statement
  // month — e.g. a check written in December that didn't clear until January.
  // Lets a treasurer pull those into the statement they actually cleared on,
  // the same way QuickBooks lets you work outstanding items into the current
  // reconciliation. Two equality filters (no orderBy) don't need a composite index.
  useEffect(() => {
    if (!account) { setOutstanding([]); return; }
    const q = query(
      collection(db, 'orgs', orgId, 'transactions'),
      where('account', '==', account),
      where('cleared', '==', false),
    );
    const unsub = onSnapshot(q, (snap) => {
      const all = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const other = all
        .filter(t => t.type !== 'Starting Balance' && (t.stmtMonth || t.date?.substring(0, 7)) !== month)
        .sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setOutstanding(other);
    }, (err) => {
      console.error('Reconcile outstanding query error:', err.code, err.message);
    });
    return unsub;
  }, [account, month, orgId]);

  const reassignStatement = async (tx, newMonth) => {
    if (!newMonth) return;
    const fromMonth = tx.stmtMonth || tx.date?.substring(0, 7) || '';
    await updateDoc(doc(db, 'orgs', orgId, 'transactions', tx.id), { stmtMonth: newMonth });
    await logAudit(orgId, 'Transaction Moved to Statement', {
      account, date: tx.date, description: tx.description, from: fromMonth, to: newMonth,
    });
    setMovingTxId(null);
    setMoveToMonth('');
  };

  // Writes back to the transaction immediately, so ticking a box here shows up
  // on the Transactions page right away instead of only after Save.
  const toggleCleared = (txId) => {
    const next = !cleared[txId];
    setCleared(prev => ({ ...prev, [txId]: next }));
    updateDoc(doc(db, 'orgs', orgId, 'transactions', txId), { cleared: next }).catch(console.error);
  };

  const toggleAll = (value) => {
    const next = {};
    transactions.forEach(t => { next[t.id] = value; });
    setCleared(next);
    transactions.forEach(t => {
      updateDoc(doc(db, 'orgs', orgId, 'transactions', t.id), { cleared: value }).catch(console.error);
    });
  };

  // Calculations
  const begin = Number(beginningBalance) || 0;
  const stmt  = Number(statementBalance) || 0;

  const clearedIncome   = transactions.filter(t => cleared[t.id]).reduce((s, t) => s + (t.income || 0), 0);
  const clearedExpense  = transactions.filter(t => cleared[t.id]).reduce((s, t) => s + (t.expense || 0), 0);
  const clearedBalance  = begin + clearedIncome - clearedExpense;
  const difference      = clearedBalance - stmt;
  const isBalanced      = Math.abs(difference) < 0.005;

  const totalIncome  = transactions.reduce((s, t) => s + (t.income || 0), 0);
  const totalExpense = transactions.reduce((s, t) => s + (t.expense || 0), 0);
  const clearedCount = transactions.filter(t => cleared[t.id]).length;

  const handleSave = async () => {
    setSaving(true);
    const id = `${account}__${month}`;
    await setDoc(doc(db, 'orgs', orgId, 'reconciliations', id), {
      account,
      month,
      beginningBalance: begin,
      statementBalance: stmt,
      endingBalance: clearedBalance,
      clearedIds: cleared,
      status: isBalanced ? 'reconciled' : 'in_progress',
      savedAt: new Date().toISOString(),
    });
    // Each transaction's `cleared` field is already written the moment its
    // checkbox is toggled — clearedIds above is just a point-in-time snapshot.
    await logAudit(orgId, isBalanced ? 'Reconciliation Completed' : 'Reconciliation Saved', {
      account, month, statementBalance: stmt, endingBalance: clearedBalance,
    });
    setReconciliation({ status: isBalanced ? 'reconciled' : 'in_progress' });
    setSaving(false);
  };

  if (orgLoading) {
    return <div className="p-6 text-slate-500 text-sm">Loading…</div>;
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-slate-800">Reconcile</h2>
        <p className="text-slate-500 text-sm mt-1">
          Match your bank statement against recorded transactions.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm p-5 mb-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Account</label>
            <select
              value={account}
              onChange={e => setAccount(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {activeAccounts.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Statement Month</label>
            <input
              type="month" value={month}
              onChange={e => setMonth(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Beginning Balance ($)</label>
            <input
              type="number" step="0.01" value={beginningBalance}
              onChange={e => setBeginningBalance(e.target.value)}
              placeholder="0.00"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1">Statement Ending Balance ($)</label>
            <input
              type="number" step="0.01" value={statementBalance}
              onChange={e => setStatementBalance(e.target.value)}
              placeholder="0.00"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>
      </div>

      {/* Summary bar */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {[
          { label: 'Beginning Balance', value: fmt(begin), color: 'text-slate-700' },
          { label: 'Cleared Deposits',  value: fmt(clearedIncome),  color: 'text-green-600' },
          { label: 'Cleared Payments',  value: fmt(clearedExpense), color: 'text-red-600' },
          { label: 'Cleared Balance',   value: fmt(clearedBalance), color: 'text-slate-700' },
          {
            label: 'Difference',
            value: fmt(Math.abs(difference)),
            color: isBalanced ? 'text-green-600' : 'text-red-600',
            badge: isBalanced ? '✓ Balanced' : null,
          },
        ].map(({ label, value, color, badge }) => (
          <div key={label} className="bg-white rounded-xl shadow-sm p-4 text-center">
            <div className="text-xs text-slate-500 mb-1">{label}</div>
            <div className={`text-lg font-bold font-mono ${color}`}>{value}</div>
            {badge && (
              <div className="mt-1 text-xs font-semibold text-green-600 bg-green-50 rounded-full px-2 py-0.5 inline-block">
                {badge}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Transaction list */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-5">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <div className="text-sm font-semibold text-slate-700">
            {transactions.length} transactions · {clearedCount} cleared
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toggleAll(true)}
              className="text-xs text-blue-600 hover:underline"
            >
              Select all
            </button>
            <span className="text-gray-300">|</span>
            <button
              onClick={() => toggleAll(false)}
              className="text-xs text-blue-600 hover:underline"
            >
              Clear all
            </button>
          </div>
        </div>

        {transactions.length === 0 ? (
          <div className="px-4 py-12 text-center text-slate-500 text-sm">
            No transactions found for {account} in {month}.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
                  <th className="px-4 py-2 text-center font-semibold w-10">✓</th>
                  <th className="px-4 py-2 text-left font-semibold">Date</th>
                  <th className="px-4 py-2 text-left font-semibold">Chk #</th>
                  <th className="px-4 py-2 text-left font-semibold">Description</th>
                  <th className="px-4 py-2 text-left font-semibold">Fund / Type</th>
                  <th className="px-4 py-2 text-right font-semibold text-red-600">Payment</th>
                  <th className="px-4 py-2 text-right font-semibold text-green-600">Deposit</th>
                  <th className="px-4 py-2 text-left font-semibold">Statement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx) => {
                  const isCleared = !!cleared[tx.id];
                  const isMoving = movingTxId === tx.id;
                  return (
                    <tr
                      key={tx.id}
                      onClick={() => toggleCleared(tx.id)}
                      className={`cursor-pointer transition-colors ${
                        isCleared ? 'bg-green-50/50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <td className="px-4 py-2.5 text-center">
                        <input
                          type="checkbox"
                          checked={isCleared}
                          onChange={() => toggleCleared(tx.id)}
                          onClick={e => e.stopPropagation()}
                          className="rounded"
                        />
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{tx.date}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{tx.checkNum}</td>
                      <td className="px-4 py-2.5 max-w-[200px] truncate" title={tx.description}>
                        {tx.description}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-slate-500">{tx.classification}</span>
                        <span className="text-xs text-slate-500 mx-1">·</span>
                        <span className="text-xs text-slate-600">{tx.type}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-red-600 font-medium">
                        {tx.expense > 0 ? fmt(tx.expense) : ''}
                      </td>
                      <td className="px-4 py-2.5 text-right text-green-600 font-medium">
                        {tx.income > 0 ? fmt(tx.income) : ''}
                      </td>
                      <td className="px-4 py-2.5" onClick={e => e.stopPropagation()}>
                        {isMoving ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="month" value={moveToMonth} autoFocus
                              onChange={e => setMoveToMonth(e.target.value)}
                              className="border border-gray-300 rounded-lg px-1.5 py-1 text-xs w-28 focus:outline-none focus:ring-2 focus:ring-blue-300"
                            />
                            <button
                              onClick={() => reassignStatement(tx, moveToMonth)}
                              disabled={!moveToMonth}
                              className="text-xs text-blue-600 hover:underline disabled:opacity-40 disabled:no-underline"
                            >
                              Move
                            </button>
                            <button
                              onClick={() => { setMovingTxId(null); setMoveToMonth(''); }}
                              className="text-xs text-slate-400 hover:underline"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setMovingTxId(tx.id); setMoveToMonth(month); }}
                            className="text-xs text-slate-400 hover:text-blue-600 hover:underline"
                          >
                            Move…
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-100 font-semibold border-t-2 border-slate-200 text-sm">
                  <td colSpan={5} className="px-4 py-2 text-slate-500">
                    Totals ({transactions.length})
                  </td>
                  <td className="px-4 py-2 text-right text-red-600">{fmt(totalExpense)}</td>
                  <td className="px-4 py-2 text-right text-green-600">{fmt(totalIncome)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Outstanding items from other statement months */}
      {outstanding.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-5">
          <button
            onClick={() => setShowOutstanding(s => !s)}
            className="w-full flex items-center justify-between px-4 py-3 border-b border-gray-200 text-left"
          >
            <span className="text-sm font-semibold text-slate-700">
              {outstanding.length} outstanding item{outstanding.length === 1 ? '' : 's'} from other statements
            </span>
            <span className="text-xs text-blue-600">{showOutstanding ? 'Hide' : 'Show'}</span>
          </button>
          {showOutstanding && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-gray-200 text-slate-600">
                    <th className="px-4 py-2 text-left font-semibold">Date</th>
                    <th className="px-4 py-2 text-left font-semibold">Chk #</th>
                    <th className="px-4 py-2 text-left font-semibold">Description</th>
                    <th className="px-4 py-2 text-left font-semibold">Fund / Type</th>
                    <th className="px-4 py-2 text-right font-semibold text-red-600">Payment</th>
                    <th className="px-4 py-2 text-right font-semibold text-green-600">Deposit</th>
                    <th className="px-4 py-2 text-left font-semibold">Currently in</th>
                    <th className="px-4 py-2 text-left font-semibold"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {outstanding.map((tx) => (
                    <tr key={tx.id}>
                      <td className="px-4 py-2.5 font-mono text-xs whitespace-nowrap">{tx.date}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{tx.checkNum}</td>
                      <td className="px-4 py-2.5 max-w-[200px] truncate" title={tx.description}>{tx.description}</td>
                      <td className="px-4 py-2.5">
                        <span className="text-xs text-slate-500">{tx.classification}</span>
                        <span className="text-xs text-slate-500 mx-1">·</span>
                        <span className="text-xs text-slate-600">{tx.type}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right text-red-600 font-medium">{tx.expense > 0 ? fmt(tx.expense) : ''}</td>
                      <td className="px-4 py-2.5 text-right text-green-600 font-medium">{tx.income > 0 ? fmt(tx.income) : ''}</td>
                      <td className="px-4 py-2.5 text-xs text-slate-500 font-mono">{tx.stmtMonth || tx.date?.substring(0, 7)}</td>
                      <td className="px-4 py-2.5">
                        <button
                          onClick={() => reassignStatement(tx, month)}
                          className="text-xs text-blue-600 hover:underline whitespace-nowrap"
                        >
                          Include in {month}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-4">
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save Progress'}
        </button>
        {isBalanced && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-green-700 transition-colors disabled:opacity-50"
          >
            Mark Reconciled
          </button>
        )}
        {reconciliation && (
          <span className={`text-sm font-medium ${
            reconciliation.status === 'reconciled' ? 'text-green-600' : 'text-slate-500'
          }`}>
            {reconciliation.status === 'reconciled' ? '✓ Reconciled' : 'In progress'}
          </span>
        )}
      </div>
    </div>
  );
};

export default Reconcile;
